/**
 * Fabrication de geometrie procedurale.
 *
 * Tout le contenu visuel du jeu est genere: pas un seul fichier de modele ni de
 * texture. On assemble donc des primitives en une geometrie unique, avec des
 * couleurs de sommets pour differencier les parties, ce qui permet ensuite de
 * dessiner des milliers d'exemplaires en un seul appel via `InstancedMesh`.
 */
import * as THREE from 'three'

export interface Partie {
  geo: THREE.BufferGeometry
  couleur: number
  transforme: THREE.Matrix4
}

/**
 * Fusionne des primitives transformees en une geometrie non indexee portant un
 * attribut `color`. Les geometries d'entree sont liberees: elles ne servent
 * qu'a la construction.
 */
export function fusionner(parties: Partie[]): THREE.BufferGeometry {
  const positions: number[] = []
  const normales: number[] = []
  const couleurs: number[] = []
  const matNormale = new THREE.Matrix3()
  const teinte = new THREE.Color()
  const v = new THREE.Vector3()
  const n = new THREE.Vector3()

  for (const partie of parties) {
    const geo = partie.geo.index ? partie.geo.toNonIndexed() : partie.geo
    const pos = geo.getAttribute('position')
    const nor = geo.getAttribute('normal')
    matNormale.getNormalMatrix(partie.transforme)
    teinte.setHex(partie.couleur)

    for (let i = 0; i < pos.count; i++) {
      v.fromBufferAttribute(pos, i).applyMatrix4(partie.transforme)
      positions.push(v.x, v.y, v.z)
      n.fromBufferAttribute(nor, i).applyMatrix3(matNormale).normalize()
      normales.push(n.x, n.y, n.z)
      couleurs.push(teinte.r, teinte.g, teinte.b)
    }
    if (geo !== partie.geo) geo.dispose()
    partie.geo.dispose()
  }

  const fusion = new THREE.BufferGeometry()
  fusion.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3))
  fusion.setAttribute('normal', new THREE.Float32BufferAttribute(normales, 3))
  fusion.setAttribute('color', new THREE.Float32BufferAttribute(couleurs, 3))
  fusion.computeBoundingSphere()
  return fusion
}

/** Matrice de transformation compacte, pour decrire une partie lisiblement. */
export function tr(
  position?: [number, number, number],
  rotation?: [number, number, number],
  echelle?: [number, number, number],
): THREE.Matrix4 {
  const mat = new THREE.Matrix4()
  const q = new THREE.Quaternion()
  if (rotation) q.setFromEuler(new THREE.Euler(rotation[0], rotation[1], rotation[2]))
  mat.compose(
    new THREE.Vector3(...(position ?? [0, 0, 0])),
    q,
    new THREE.Vector3(...(echelle ?? [1, 1, 1])),
  )
  return mat
}

/**
 * Deforme une geometrie de facon deterministe, pour casser la regularite des
 * solides platoniciens (un rocher ne doit pas ressembler a un de a douze faces).
 */
export function bosseler(geo: THREE.BufferGeometry, force: number, graine = 1): THREE.BufferGeometry {
  const pos = geo.getAttribute('position')
  for (let i = 0; i < pos.count; i++) {
    const bruit = (Math.sin((i + graine) * 12.9898) * 43758.5453) % 1
    const f = 1 - force / 2 + Math.abs(bruit) * force
    pos.setXYZ(i, pos.getX(i) * f, pos.getY(i) * f, pos.getZ(i) * f)
  }
  geo.computeVertexNormals()
  return geo
}
